import { useSelector } from 'react-redux';
import { selectAccountData } from '../redux/main/accountSlice'; // Assicurati che il percorso sia corretto

// Funzione helper per scurire un colore esadecimale di una data percentuale
const darkenColor = (hexColor, percent) => {
    if (!hexColor || typeof hexColor !== 'string' || hexColor.length < 6) return '#000000';
    let hex = hexColor.startsWith('#') ? hexColor.slice(1) : hexColor;
    if (hex.length !== 6 && hex.length !== 3) return '#000000';

    let r, g, b;
    if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
    } else {
        r = parseInt(hex.slice(0, 2), 16);
        g = parseInt(hex.slice(2, 4), 16);
        b = parseInt(hex.slice(4, 6), 16);
    }

    const amount = Math.floor(255 * (percent / 100));

    r = Math.max(0, r - amount);
    g = Math.max(0, g - amount);
    b = Math.max(0, b - amount);

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Funzione helper per determinare il colore del testo (bianco o nero) per il contrasto
// Basato sulla luminanza percepita.
const getTextColorForBackground = (hexColor) => {
    if (!hexColor || typeof hexColor !== 'string' || hexColor.length < 6) return '#000000'; // Default a nero se il colore non è valido

    const hex = hexColor.startsWith('#') ? hexColor.slice(1) : hexColor;
    if (hex.length !== 6 && hex.length !== 3) return '#000000'; // Supporta formati #RGB e #RRGGBB

    let r, g, b;
    if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
    } else {
        r = parseInt(hex.slice(0, 2), 16);
        g = parseInt(hex.slice(2, 4), 16);
        b = parseInt(hex.slice(4, 6), 16);
    }

    // Formula per la luminanza YIQ
    const luminance = (r * 299 + g * 587 + b * 114) / 1000;
    return luminance >= 128 ? '#000000' : '#FFFFFF'; // Nero per sfondi chiari (luminanza >= 128), bianco per scuri
};

export const useColorPalette = () => {
    const accountData = useSelector(selectAccountData);
    // DISABILITATO: Ignora completamente i colori dal database
    // const colorsSchema = accountData?.settings?.UI?.colorsSchema;
    const colorsSchema = null; // Forza l'uso dei colori di fallback

    // Definisci qui i tuoi colori di fallback
    const fallbackColors = {
        primary: '#383838',       // Grigio scuro
        secondary: '#5a5a5a',     // Grigio medio
        accent: '#808080',        // Grigio chiaro
        background: '#f5f5f5',    // Grigio molto chiaro
        tableHeader: '#4a4a4a',   // Grigio scuro per header tabelle
        tableRowDark: '#e8e8e8',  // Grigio chiaro per righe alterne
    };
    
    // Funzione helper per accedere in sicurezza a proprietà annidate dell'oggetto colorsSchema
    const getNestedColor = (path, fallback) => {
        if (!colorsSchema) return fallback;
        
        const keys = path.split('.');
        let value = colorsSchema;
        
        for (const key of keys) {
            if (!value || typeof value !== 'object') return fallback;
            value = value[key];
            if (value === undefined) return fallback;
        }
        
        return value || fallback;
    };
    
    // Valori di default per la palette
    let palette = {
        // Colori principali
        primary: fallbackColors.primary,
        textOnPrimary: getTextColorForBackground(fallbackColors.primary),
        secondary: fallbackColors.secondary,
        textOnSecondary: getTextColorForBackground(fallbackColors.secondary),
        accent: fallbackColors.accent,
        textOnAccent: getTextColorForBackground(fallbackColors.accent),
        background: fallbackColors.background,
        textOnBackground: getTextColorForBackground(fallbackColors.background),
        
        // Tabelle
        tableHeaderBg: fallbackColors.tableHeader,
        tableHeaderText: getTextColorForBackground(fallbackColors.tableHeader),
        tableRowLight: fallbackColors.background,
        textOnTableRowLight: getTextColorForBackground(fallbackColors.background),
        tableRowDark: fallbackColors.tableRowDark,
        textOnTableRowDark: getTextColorForBackground(fallbackColors.tableRowDark),
        
        // Highlight
        highlightBg: fallbackColors.accent,
        textOnHighlight: getTextColorForBackground(fallbackColors.accent),
        
        // Pulsanti
        buttonPrimaryBg: fallbackColors.primary,
        buttonPrimaryText: getTextColorForBackground(fallbackColors.primary),
        buttonPrimaryHover: darkenColor(fallbackColors.primary, 10),
        
        buttonSecondaryBg: fallbackColors.secondary,
        buttonSecondaryText: getTextColorForBackground(fallbackColors.secondary),
        buttonSecondaryHover: darkenColor(fallbackColors.secondary, 10),
        
        buttonDangerBg: '#bc4749',
        buttonDangerText: '#ffffff',
        buttonDangerHover: '#a73f41',
        
        buttonSuccessBg: '#4f772d',
        buttonSuccessText: '#ffffff',
        buttonSuccessHover: '#3e5f24',
        
        buttonDisabledBg: '#c5c5c5',
        buttonDisabledText: '#7a7a7a',
        
        // Icone
        iconDefault: '#6b705c',
        iconPrimary: fallbackColors.primary,
        iconSecondary: fallbackColors.secondary,
        iconWarning: '#ffbf3d',
        iconError: '#bc4749',
        iconSuccess: '#4f772d',
        
        // Form
        inputBg: '#ffffff',
        inputBorder: '#ced4da',
        inputBorderFocus: fallbackColors.accent,
        inputText: '#495057',
        
        // Stato
        stateHover: '#f0f2f5',
        stateFocus: '#e2e9e0',
        stateActive: '#d9e3d5',
        stateSelected: '#c9d8c2',
        
        // Badge
        badgeBg: '#e9ecef',
        badgeText: '#212529',
    };
    
    // Se abbiamo uno schema colori dal database, sovrascriviamo i valori di default
    if (colorsSchema && typeof colorsSchema === 'object') {
        // Colori base
        const primaryColor = getNestedColor('primary', fallbackColors.primary);
        const secondaryColor = getNestedColor('secondary', fallbackColors.secondary);
        const accentColor = getNestedColor('accent', fallbackColors.accent);
        const backgroundColor = getNestedColor('background', fallbackColors.background);
        const tableHeaderColor = getNestedColor('tableHeader', fallbackColors.tableHeader);
        const tableRowDarkColor = getNestedColor('tableRowDark', fallbackColors.tableRowDark);
        
        palette = {
            // Colori base
            primary: primaryColor,
            textOnPrimary: getTextColorForBackground(primaryColor),
            secondary: secondaryColor,
            textOnSecondary: getTextColorForBackground(secondaryColor),
            accent: accentColor,
            textOnAccent: getTextColorForBackground(accentColor),
            background: backgroundColor,
            textOnBackground: getTextColorForBackground(backgroundColor),
            
            // Tabelle
            tableHeaderBg: tableHeaderColor,
            tableHeaderText: getTextColorForBackground(tableHeaderColor),
            tableRowLight: backgroundColor,
            textOnTableRowLight: getTextColorForBackground(backgroundColor),
            tableRowDark: tableRowDarkColor,
            textOnTableRowDark: getTextColorForBackground(tableRowDarkColor),
            
            // Highlight
            highlightBg: accentColor,
            textOnHighlight: getTextColorForBackground(accentColor),
            
            // Pulsanti - Usa i valori dal nuovo schema o fallback ai predefiniti
            buttonPrimaryBg: getNestedColor('buttons.primary.background', primaryColor),
            buttonPrimaryText: getNestedColor('buttons.primary.text', getTextColorForBackground(primaryColor)),
            buttonPrimaryHover: getNestedColor('buttons.primary.hover', darkenColor(primaryColor, 10)),
            
            buttonSecondaryBg: getNestedColor('buttons.secondary.background', secondaryColor),
            buttonSecondaryText: getNestedColor('buttons.secondary.text', getTextColorForBackground(secondaryColor)),
            buttonSecondaryHover: getNestedColor('buttons.secondary.hover', darkenColor(secondaryColor, 10)),
            
            buttonDangerBg: getNestedColor('buttons.danger.background', '#bc4749'),
            buttonDangerText: getNestedColor('buttons.danger.text', '#ffffff'),
            buttonDangerHover: getNestedColor('buttons.danger.hover', '#a73f41'),
            
            buttonSuccessBg: getNestedColor('buttons.success.background', '#4f772d'),
            buttonSuccessText: getNestedColor('buttons.success.text', '#ffffff'),
            buttonSuccessHover: getNestedColor('buttons.success.hover', '#3e5f24'),
            
            buttonDisabledBg: getNestedColor('buttons.disabled.background', '#c5c5c5'),
            buttonDisabledText: getNestedColor('buttons.disabled.text', '#7a7a7a'),
            
            // Icone
            iconDefault: getNestedColor('icons.default', '#6b705c'),
            iconPrimary: getNestedColor('icons.primary', primaryColor),
            iconSecondary: getNestedColor('icons.secondary', secondaryColor),
            iconWarning: getNestedColor('icons.warning', '#ffbf3d'),
            iconError: getNestedColor('icons.error', '#bc4749'),
            iconSuccess: getNestedColor('icons.success', '#4f772d'),
            
            // Form
            inputBg: getNestedColor('forms.input.background', '#ffffff'),
            inputBorder: getNestedColor('forms.input.border', '#ced4da'),
            inputBorderFocus: getNestedColor('forms.input.borderFocus', accentColor),
            inputText: getNestedColor('forms.input.text', '#495057'),
            
            // Stato
            stateHover: getNestedColor('states.hover', '#f0f2f5'),
            stateFocus: getNestedColor('states.focus', '#e2e9e0'),
            stateActive: getNestedColor('states.active', '#d9e3d5'),
            stateSelected: getNestedColor('states.selected', '#c9d8c2'),
            
            // Badge
            badgeBg: getNestedColor('badge.default.background', '#e9ecef'),
            badgeText: getNestedColor('badge.default.text', '#212529'),
            
            // Manteniamo una copia grezza dello schema colori completo
            raw: colorsSchema
        };
    }

    return palette;
};
